import { Injectable, inject } from '@angular/core';
import { Observable, distinctUntilChanged, shareReplay, skip } from 'rxjs';
import {
  DomainSignalId,
  DomainSignalKeys,
  SIGNAL_DOMAIN_BY_ID,
  SIGNAL_ENTITIES_COLLECTION,
  SIGNALS_ROOT_COLLECTION,
  resolveDomainSignalChange,
  type DomainSignal,
  type DomainSignalChange,
} from '@ag-common-lib/public-api';
import { FirestoreSignalsService } from './firestore-signals.service';

/**
 * Subscribes to the change signals the backend publishes, for any domain.
 *
 * A signal carries no data — only a version that moves whenever the underlying SQL
 * rows change. Consumers re-read through the API, which keeps permission checks on
 * the server where they can be enforced; nothing here is authoritative. That is what
 * makes it safe for authorization itself to ride on this mechanism.
 *
 * Replaces ReportingModuleSignalsService, which hardcoded a reporting path and
 * duplicated the subscribe/map/distinct logic that FirestoreSignalsService already
 * owns.
 */
@Injectable({ providedIn: 'root' })
export class DomainSignalsService {
  private readonly firestoreSignals = inject(FirestoreSignalsService);

  /**
   * One shared stream per signal, not one per subscriber.
   *
   * This cache is the reason the service exists rather than callers using
   * FirestoreSignalsService directly: that service opens a fresh Firestore listener
   * per subscription, so a signal watched from several components — which is exactly
   * how permissions will be watched — would hold several listeners on one document.
   *
   * `refCount: true` tears the listener down when the last subscriber leaves, and
   * re-opens it on the next one.
   */
  private readonly versionBySignalId = new Map<DomainSignalId, Observable<number>>();

  /**
   * Same one-listener-per-signal caching as `versionBySignalId`, for the readers that
   * need to know WHICH rows moved rather than just that something did.
   */
  private readonly changesBySignalId = new Map<DomainSignalId, Observable<DomainSignalChange>>();

  /**
   * Current version, starting with whatever the document holds now. 0 when the
   * document does not exist yet — a domain that has never emitted, which is not an
   * error.
   */
  getSignalVersion(signalId: DomainSignalId): Observable<number> {
    const cached = this.versionBySignalId.get(signalId);
    if (cached) {
      return cached;
    }

    const version$ = this.firestoreSignals
      .watchSignalVersion<DomainSignal>({
        collectionPath: this.getCollectionPath(signalId),
        documentId: signalId,
        // Guards against a negative or non-numeric version as well as a missing one:
        // the value is only ever compared, so anything unusable is treated as "no
        // signal yet" rather than propagated.
        mapData: signal => {
          const version = Number(signal?.[DomainSignalKeys.version] ?? 0);

          return Number.isFinite(version) && version >= 0 ? version : 0;
        },
        fallbackVersion: 0,
      })
      .pipe(
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    this.versionBySignalId.set(signalId, version$);

    return version$;
  }

  /**
   * Emits only on CHANGES after subscribing, which is what a "reload" consumer
   * wants — the first value describes the state it already rendered, so acting on it
   * would cause an immediate redundant fetch.
   */
  onChanged(signalId: DomainSignalId): Observable<number> {
    return this.getSignalVersion(signalId).pipe(skip(1));
  }

  /**
   * Changes, each carrying the rows it covers.
   *
   * `isCoarse` is the part that matters: it is true whenever the keys cannot account for
   * the whole version jump, and a reader that ignores it will leave stale rows on screen
   * after a backfill or a reconnect. The rule itself lives with the document shape, in
   * `resolveDomainSignalChange`, because the publisher has to agree with it.
   *
   * Emits only after the first snapshot, for the same reason `onChanged` does: the state
   * a subscriber starts from is the state it already has.
   */
  onChangedRows(signalId: DomainSignalId): Observable<DomainSignalChange> {
    const cached = this.changesBySignalId.get(signalId);
    if (cached) {
      return cached;
    }

    let previousVersion: number | null = null;

    const changes$ = this.firestoreSignals
      .watchSignal<DomainSignal, DomainSignalChange>({
        collectionPath: this.getCollectionPath(signalId),
        documentId: signalId,
        mapData: signal => {
          const rawVersion = Number(signal?.[DomainSignalKeys.version] ?? 0);
          const currentVersion = Number.isFinite(rawVersion) && rawVersion >= 0 ? rawVersion : 0;
          // The first snapshot has nothing to compare against, so it is measured against
          // itself: a zero delta, which resolves to coarse and is then dropped by skip(1).
          const change = resolveDomainSignalChange({
            previousVersion: previousVersion ?? currentVersion,
            currentVersion,
            changedRowKeys: signal?.[DomainSignalKeys.changedRowKeys],
          });
          previousVersion = currentVersion;

          return change;
        },
        distinctComparator: (previous, current) => previous.version === current.version,
      })
      .pipe(skip(1), shareReplay({ bufferSize: 1, refCount: true }));

    this.changesBySignalId.set(signalId, changes$);

    return changes$;
  }

  private getCollectionPath(signalId: DomainSignalId): string {
    return [SIGNALS_ROOT_COLLECTION, SIGNAL_DOMAIN_BY_ID[signalId], SIGNAL_ENTITIES_COLLECTION].join('/');
  }
}
